// Data-format converters (JSON -> XML, CSV -> XML, JSON -> YAML). Pure and
// browser-local, no dependencies.

export type DataFormatVariant =
  | 'json-to-xml'
  | 'csv-to-xml'
  | 'json-to-yaml'
  | 'json-to-text'
  | 'json-to-tsv'
  | 'tsv-to-json'
  | 'url-parser';

export const dataFormatMeta: Record<
  DataFormatVariant,
  { badge: string; title: string; inputLabel: string; outputLabel: string; sample: string }
> = {
  'json-to-xml': {
    badge: 'JSON',
    title: 'Convert JSON to XML',
    inputLabel: 'JSON',
    outputLabel: 'XML',
    sample: '{\n  "name": "Ada",\n  "roles": ["admin", "editor"]\n}',
  },
  'csv-to-xml': {
    badge: 'CSV',
    title: 'Convert CSV to XML',
    inputLabel: 'CSV (first row is headers)',
    outputLabel: 'XML',
    sample: 'name,role\nAda,admin\nGrace,editor',
  },
  'json-to-yaml': {
    badge: 'JSON',
    title: 'Convert JSON to YAML',
    inputLabel: 'JSON',
    outputLabel: 'YAML',
    sample: '{\n  "name": "Ada",\n  "roles": ["admin", "editor"]\n}',
  },
  'json-to-text': {
    badge: 'JSON',
    title: 'Convert JSON to plain text',
    inputLabel: 'JSON',
    outputLabel: 'Flattened text',
    sample: '{\n  "name": "Ada",\n  "roles": ["admin", "editor"]\n}',
  },
  'json-to-tsv': {
    badge: 'JSON',
    title: 'Convert JSON to TSV',
    inputLabel: 'JSON array of objects',
    outputLabel: 'TSV',
    sample: '[\n  {"name": "Ada", "role": "admin"},\n  {"name": "Grace", "role": "editor"}\n]',
  },
  'tsv-to-json': {
    badge: 'TSV',
    title: 'Convert TSV to JSON',
    inputLabel: 'TSV (first row is headers, tab-separated)',
    outputLabel: 'JSON',
    sample: 'name\trole\nAda\tadmin\nGrace\teditor',
  },
  'url-parser': {
    badge: 'URL',
    title: 'Parse a URL into parts',
    inputLabel: 'URL',
    outputLabel: 'Parsed parts',
    sample: 'https://user@example.com:8080/path/page?q=hello&page=2#section',
  },
};

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function safeTag(name: string): string {
  const cleaned = name.replace(/[^a-zA-Z0-9_.-]/g, '_');
  return /^[a-zA-Z_]/.test(cleaned) ? cleaned : `_${cleaned}`;
}

function jsonToXml(value: unknown, tag: string, indent: string): string {
  const pad = indent;
  if (Array.isArray(value)) {
    return value.map((item) => jsonToXml(item, tag, indent)).join('\n');
  }
  if (value !== null && typeof value === 'object') {
    const inner = Object.entries(value as Record<string, unknown>)
      .map(([key, child]) => jsonToXml(child, safeTag(key), indent + '  '))
      .join('\n');
    return `${pad}<${tag}>\n${inner}\n${pad}</${tag}>`;
  }
  const text = value === null || value === undefined ? '' : escapeXml(String(value));
  return `${pad}<${tag}>${text}</${tag}>`;
}

export function convertJsonToXml(input: string): string {
  let parsed: unknown;
  try {
    parsed = JSON.parse(input);
  } catch {
    throw new Error('Enter valid JSON to convert.');
  }
  return `<?xml version="1.0" encoding="UTF-8"?>\n${jsonToXml(parsed, 'root', '')}`;
}

// Minimal CSV row parser handling quoted fields with commas and escaped quotes.
function parseCsvLine(line: string): string[] {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (inQuotes) {
      if (char === '"' && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        current += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ',') {
      fields.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  fields.push(current);
  return fields;
}

export function convertCsvToXml(input: string): string {
  const lines = input.split(/\r?\n/).filter((line) => line.trim() !== '');
  if (lines.length < 2) {
    throw new Error('Enter CSV with a header row and at least one data row.');
  }
  const headers = parseCsvLine(lines[0]).map((header) => safeTag(header.trim() || 'column'));
  const rows = lines.slice(1).map((line) => {
    const cells = parseCsvLine(line);
    const inner = headers
      .map(
        (header, index) => `    <${header}>${escapeXml((cells[index] ?? '').trim())}</${header}>`,
      )
      .join('\n');
    return `  <row>\n${inner}\n  </row>`;
  });
  return `<?xml version="1.0" encoding="UTF-8"?>\n<rows>\n${rows.join('\n')}\n</rows>`;
}

function yamlScalar(value: unknown): string {
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'boolean' || typeof value === 'number') return String(value);
  const text = String(value);
  if (text === '' || /[:#\-?{}[\],&*!|>'"%@`]/.test(text) || /^\s|\s$/.test(text)) {
    return JSON.stringify(text);
  }
  return text;
}

function jsonToYaml(value: unknown, indent: number): string {
  const pad = '  '.repeat(indent);
  if (Array.isArray(value)) {
    if (value.length === 0) return `${pad}[]`;
    return value
      .map((item) => {
        if (item !== null && typeof item === 'object') {
          return `${pad}-\n${jsonToYaml(item, indent + 1)}`;
        }
        return `${pad}- ${yamlScalar(item)}`;
      })
      .join('\n');
  }
  if (value !== null && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return `${pad}{}`;
    return entries
      .map(([key, child]) => {
        if (child !== null && typeof child === 'object') {
          return `${pad}${key}:\n${jsonToYaml(child, indent + 1)}`;
        }
        return `${pad}${key}: ${yamlScalar(child)}`;
      })
      .join('\n');
  }
  return `${pad}${yamlScalar(value)}`;
}

export function convertJsonToYaml(input: string): string {
  let parsed: unknown;
  try {
    parsed = JSON.parse(input);
  } catch {
    throw new Error('Enter valid JSON to convert.');
  }
  return jsonToYaml(parsed, 0);
}

function flattenJson(value: unknown, prefix: string, lines: string[]): void {
  if (Array.isArray(value)) {
    value.forEach((item, i) => flattenJson(item, prefix ? `${prefix}[${i}]` : `[${i}]`, lines));
  } else if (value !== null && typeof value === 'object') {
    for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
      flattenJson(child, prefix ? `${prefix}.${key}` : key, lines);
    }
  } else {
    lines.push(`${prefix}: ${value === null || value === undefined ? '' : String(value)}`);
  }
}

export function convertJsonToText(input: string): string {
  let parsed: unknown;
  try {
    parsed = JSON.parse(input);
  } catch {
    throw new Error('Enter valid JSON to convert.');
  }
  const lines: string[] = [];
  flattenJson(parsed, '', lines);
  return lines.join('\n');
}

export function convertJsonToTsv(input: string): string {
  let parsed: unknown;
  try {
    parsed = JSON.parse(input);
  } catch {
    throw new Error('Enter valid JSON to convert.');
  }
  const rows = Array.isArray(parsed) ? parsed : [parsed];
  if (!rows.length) return '';
  const headers: string[] = [];
  for (const row of rows) {
    if (row && typeof row === 'object' && !Array.isArray(row)) {
      for (const key of Object.keys(row as Record<string, unknown>)) {
        if (!headers.includes(key)) headers.push(key);
      }
    }
  }
  if (!headers.length) {
    throw new Error('Enter a JSON array of objects.');
  }
  const cell = (value: unknown): string => {
    if (value === null || value === undefined) return '';
    const text = typeof value === 'object' ? JSON.stringify(value) : String(value);
    return text.replace(/\t/g, ' ').replace(/\r?\n/g, ' ');
  };
  const lines = [headers.join('\t')];
  for (const row of rows) {
    const record = (row ?? {}) as Record<string, unknown>;
    lines.push(headers.map((header) => cell(record[header])).join('\t'));
  }
  return lines.join('\n');
}

export function convertTsvToJson(input: string): string {
  const lines = input
    .replace(/\r\n/g, '\n')
    .split('\n')
    .filter((line) => line.length > 0);
  if (lines.length < 2) {
    throw new Error('Enter TSV with a header row and at least one data row.');
  }
  const headers = lines[0].split('\t');
  const records = lines.slice(1).map((line) => {
    const cells = line.split('\t');
    const record: Record<string, string> = {};
    headers.forEach((header, i) => {
      record[header] = cells[i] ?? '';
    });
    return record;
  });
  return JSON.stringify(records, null, 2);
}

export function parseUrl(input: string): string {
  let url: URL;
  try {
    url = new URL(input.trim());
  } catch {
    throw new Error('Enter a full URL, including the scheme (for example https://).');
  }
  const lines = [
    `Protocol: ${url.protocol.replace(/:$/, '')}`,
    `Host: ${url.host}`,
    `Hostname: ${url.hostname}`,
    `Port: ${url.port || '(default)'}`,
    `Path: ${url.pathname}`,
    `Hash: ${url.hash || '(none)'}`,
    `Username: ${url.username || '(none)'}`,
  ];
  const params = Array.from(url.searchParams.entries());
  if (params.length) {
    lines.push('Query parameters:');
    for (const [key, value] of params) lines.push(`  ${key} = ${value}`);
  } else {
    lines.push('Query parameters: (none)');
  }
  return lines.join('\n');
}

export function convertDataFormat(variant: DataFormatVariant, input: string): string {
  if (!input.trim()) return '';
  if (variant === 'json-to-xml') return convertJsonToXml(input);
  if (variant === 'csv-to-xml') return convertCsvToXml(input);
  if (variant === 'json-to-text') return convertJsonToText(input);
  if (variant === 'json-to-tsv') return convertJsonToTsv(input);
  if (variant === 'tsv-to-json') return convertTsvToJson(input);
  if (variant === 'url-parser') return parseUrl(input);
  return convertJsonToYaml(input);
}
